import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IExecutingAnalysisInfo } from '../../../../redux/slices/executingAnalyses';
import { theme } from '../../../../styles/theme';

interface IProps {
  executionInfo: IExecutingAnalysisInfo;
}

const estimateProgressPercent = (
  elapsedSeconds: number,
  estimatedDuration: number,
) => {
  if (elapsedSeconds < 0) {
    return 0; // possible clock skew ...
  }
  if (estimatedDuration <= 0) {
    return 99;
  }
  // slow down after 95% if we have underestimated the duration
  if (elapsedSeconds >= estimatedDuration) {
    const over = elapsedSeconds - estimatedDuration;

    if (over <= 30) {
      // at 96% for 30 sec
      return 96;
    } else if (over <= 90) {
      // at 97% for 60 sec
      return 97;
    } else if (over <= 210) {
      // at 98% for 120 sec
      return 98;
    } else {
      // stuck at 99% if underestimated with more than 3 and a half minutes
      return 99;
    }
  }

  return Math.floor(Math.min(100, (elapsedSeconds / estimatedDuration) * 100));
};

const Container = styled.div`
  font-weight: 500;
  color: ${theme.colors.secondary900};
`;

const ChartExecutionProgress = ({ executionInfo }: IProps) => {
  const [progress, setProgress] = useState(0);
  const { executedAt, estimatedDuration } = executionInfo;

  useEffect(() => {
    const intervalId = setInterval(() => {
      const startTime = new Date(executedAt).getTime();
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      const estimatedProgress = estimateProgressPercent(
        elapsedSeconds,
        estimatedDuration,
      );
      setProgress(estimatedProgress);
    }, 1000); // Update every second

    return () => {
      clearInterval(intervalId);
    };
  }, [executedAt, estimatedDuration]);

  return <Container>{progress}%</Container>;
};

export default ChartExecutionProgress;
